import colors from 'lib/constants/resourceColors';
import React from 'react';
import { form as siteForm, createActions as siteCreateActions } from 'resources/site/';
import formatPhoneNumber from 'lib/helpers/formatPhoneNumber';
import invoiceBadgeStyle from 'lib/helpers/invoiceBadgeStyle';
import api from 'api';
import Privileges from '../lib/constants/privileges';

// Maps the on-screen customer-type filter to the report scope param.
// Default (Project Customers / unset) preserves the original project-only report.
function customerScope(state) {
    const opt = state && state.selected_filter_option;
    if(opt === 'Non-Projects') return 'nonproject';
    if(opt === 'All') return 'all';
    return 'project';
}

function statusBadgeStyle(value, data){
    const { status } = data;

    if(status === 'closed') {
        return {status: 'default', text: 'Closed'};

    } else if (status === 'open'){
        return {status: 'success', text: 'Open'}; 

    } else if (status === 'void') {
        return {status: 'error', text: 'Void'}; 
    } else { 
        return null;
    }
}
// Validate a comma-separated list of email addresses (primary + CC). Blank is allowed
// (blank = no auto-send, per invoicing decisions #36-41). Each address must have the
// standard local@domain.tld shape; commas separate addresses so they are not allowed
// inside an address.
const EMAIL_RE = /^[^\s@,]+@[^\s@,]+\.[^\s@,]+$/;
const emailListValidator = (rule, value, callback) => {
    if(!value || !value.trim()) {
        return callback();
    }
    const bad = value.split(',')
        .map(e => e.trim())
        .filter(e => e.length)
        .filter(e => !EMAIL_RE.test(e));
    if(bad.length) {
        return callback(`Invalid email address: ${bad.join(', ')}`);
    }
    callback();
};

const form = [
    {key: 'customer_number', label: 'Customer Number', writePrivileges: [Privileges.WRITE_CUSTOMERS_CUSTOMER_NUMBER]},
    '!name',
    'care_of(C/O)=Accounts Payable',
    'code',
    {key: 'address', label: 'Address', maplink: true},
    'address2(Address Line 2)',
    'city',
    '_state{US_STATES}=Florida',
    'zipcode',
    'tax_exempt',
    {key: 'tax_exempt_expiration', label: 'Tax Exempt Expiration', type: 'date'},
    '?requires_po(Requires PO#)',
    '!_payment_term{COD|COD,DUE_ON_RECEIPT|Due On Receipt,NET30|Net 30,NET60|Net 60,NET90|Net 90,PROJECT|Project}',
    {key: 'invoice_email', label: 'Invoice Email', hint: 'Where invoices are sent. Separate multiple addresses with commas for CC. Blank = no auto-send.', rules: [{validator: emailListValidator}]},
    {key: 'statement_email', label: 'Statement Email', hint: 'Where statements are sent. Separate multiple addresses with commas for CC. Blank = no auto-send.', rules: [{validator: emailListValidator}]},
    {key: 'contract_email', label: 'Contract Email', hint: 'Where contracts and renewal notices are sent. Falls back to Statement Email, then Invoice Email, if blank. Separate multiple addresses with commas for CC.', rules: [{validator: emailListValidator}]},
    '?credit_hold',
    {key: 'contacts',
        type: 'references',
        heading: 'Contacts',
        createLabel: 'Add New Contact',
        primaryCreate: false,
        createUrl: (values) => { return `/customers/${values.id}/contacts` },
        dataUrl: '/customers/:id/contacts',
        formFields: [
                {key: 'name',  
                    required: true,
                },
                {key: 'title',  
                },
                {key: 'phone', label: 'Office Phone',
                },
                {key: 'mobile',  
                },
                {key: 'fax',  
                },
                {key: 'email',  
                },
        ],
        tableProps: {
            rowURL: '/contacts/:id',
            columns: [
                {heading: 'Name', 
                    accessor: 'name',
                },
                {heading: 'Title', 
                    accessor: 'title',
                },
                {heading: 'Office', 
                    accessor: 'phone',
                    valueFunc: ((d,v) => {
                        return formatPhoneNumber(v) || v;
                    })
                },
                {heading: 'Mobile', 
                    accessor: 'mobile',
                    valueFunc: ((d,v) => {
                        return formatPhoneNumber(v) || v;
                    })
                },
                {heading: 'Fax', 
                    accessor: 'fax',
                    valueFunc: ((d,v) => {
                        return formatPhoneNumber(v) || v;
                    })
                },
                {heading: 'Email', 
                    accessor: 'email',
                },
            ]
        }
    },

    {key: 'sites',
        type: 'references',
        heading: 'Sites',
        createLabel: 'Create New Site',
        modalValuesTransform: (values) => { 
            return {billing_customer_id: values.id}; 
        },
        create: {
            label: 'Create New Site',
            url: (values) => { return `/sites` },
            formActions: siteCreateActions, 
        },
        addExisting: {
            label: 'Add Existing Site',
            searchUrl: '/sites',
            searchFields: ['id', 'name', 'address', 'address2', 'city', 'state', 'zipcode', 'parent_site_name'],
            optionValue: (site) => {
                return site.id;
            },
            optionContent: (site) => {
                const { address2, address, city, state, zipcode, name, parent_site_name } = site;
                return <div>
                    <span style={{display: 'block', fontWeight: '600'}}>{name}</span>
                    <span style={{display: 'block', fontStyle: 'italics', fontSize: '12px'}}>{address}, {address2}, {city}, {state}, {zipcode}</span>
                    <span style={{fontStyle: 'italics', fontSize: '12px'}}>{parent_site_name}</span>
                </div>
            },
            onAdd: (values, optionValue) => {
                let params = { customer_id : values.id, site_id: optionValue };
                return api.rpc('addCustomerSiteDefaultBilling', params);
            }
        },
        primaryCreate: false,
        createUrl: (values) => { return `/sites` },
        dataUrl: '/customers/:id/sites?is_default_billing_customer=true',
        formFields: siteForm,
        tableProps: {
            rowURL: '/sites/:id', 
            columns: [{heading: 'Site Info', type:'multiple', rows: [
                        {heading: 'Name', style: 'bold', accessor:'name'},
                        {heading: 'Address', separator: ', ', style: 'subtext', accessor:['address', 'address2', 'city', 'state', 'zipcode']},
                        {heading: 'Parent Site', style: 'subtext', accessor:'parent_site_name'}
                       ]
            },
            // Reads the site's own authoritative flag (decision #96). Was
            // 'customer_site_has_credit_hold', a name no view has ever supplied, so this column
            // rendered blank; customer_sites_extended exposes `credit_hold` (the site flag)
            // alongside `customer_has_credit_hold` (the account flag) -- this column is the site tier.
            {heading: 'Site Credit Hold', accessor: 'credit_hold', type: 'boolean'}
            ],
        }
    },
    {key: 'customer_projects',
        type: 'references',
        heading: `Customer Projects`,
        readPrivileges: [Privileges.LIST_CUSTOMER_PROJECTS],
        dataUrl: '/lists/customer_projects?customer_id=:id',
        tableProps: {
            rowURL: '/projects/:id',
            columns: [
                {accessor: 'created_at', type: 'date', dateFormat: 'MMM DD, YYYY' },
                {heading: 'Project Number', accessor: 'project_number', cellWidth: '110px', style: 'bold'},
                {accessor: 'salesperson'},
                {heading: 'Description', type:'multiple', rows: [
                    {heading: 'Description', style:'bold', accessor:'description'},
                    {heading: 'Activity', style: 'subtext', accessor:'logs_last_activity_description'},
                    {heading: 'User', style: 'subtext', accessor: 'logs_last_activity_user'},
                ]},
                {heading: 'Site', type: 'multiple', rows: [
                    {heading: 'Site Name', accessor: 'site_name'},
                    {heading: 'Site Address', separator: ', ',accessor: ['site_address', 'site_address2', 'site_city', 'site_state', 'site_zipcode']}, 
                ]},
                {type:'badge', 
                    badgePropsFunc: statusBadgeStyle, 
                    heading: 'Status', 
                    accessor: 'status'},
                {heading: 'Open Balance',
                    type: 'currency',
                accessor: 'outstanding_payment_request_balance',
              privileges: [Privileges.READ_CUSTOMERS_TOTAL_OUTSTANDING_PAYMENT_REQUEST_BALANCES, Privileges.READ_PROJECTS_OUTSTANDING_PAYMENT_REQUESTS]},
                {heading: 'Pending to Request',
                    type: 'currency',
                accessor: 'balance_to_finish',
              privileges: [Privileges.READ_CUSTOMERS_TOTAL_BALANCE_TO_FINISH, Privileges.READ_PROJECTS_BALANCE_TO_FINISH]},
                {heading: 'Total Project Value',
                    type: 'currency',
                accessor: 'contract_sum_to_date',
              privileges: [Privileges.READ_PROJECTS_CONTRACT_SUM_TO_DATE]}
                ]
        }
    },
    {key: 'customer_invoices',
        type: 'references',
        heading: 'Invoices',
        readPrivileges: [Privileges.LIST_CUSTOMER_INVOICES],
        dataUrl: '/lists/customer_invoices?customer_id=:id',
        tableProps: {
            rowURL: '/invoices/:id',
            columns: [
                {heading: 'Invoice Date', accessor: 'date', type: 'date', dateFormat: 'MMM DD, YYYY', cellWidth: '100px'},
                {heading: 'Invoice #', accessor: 'invoice_number', cellWidth: '110px', style: 'bold',
                    valueFunc: (row, value) => (row && row.type) ? `${row.type}-${value}` : value},
                {heading: 'Project', accessor: 'project_description'},
                {heading: 'Site', accessor: 'site_name'},
                {heading: 'Open Balance', type: 'currency', accessor: 'open_balance'},
                {heading: 'Invoice Total', type: 'currency', accessor: 'grand_total'},
                {heading: 'Status', type: 'badge', accessor: 'invoice_status', badgePropsFunc: invoiceBadgeStyle},
            ]
        }
    },
    {key: 'customer_transactions',
        type: 'references',
        heading: 'Payments',
        // All non-voided payments (transactions) for this customer. The backend list applies a
        // notVoided filter (voided = false), so voided payments never reach this table.
        readPrivileges: [Privileges.LIST_CUSTOMER_TRANSACTIONS],
        dataUrl: '/lists/customer_transactions?customer_id=:id',
        tableProps: {
            rowURL: '/transactions/:id',
            columns: [
                {heading: 'Payment Date', accessor: 'payment_date', type: 'date', dateFormat: 'MMM DD, YYYY', cellWidth: '100px'},
                {heading: 'Payment #', accessor: 'transaction_number', cellWidth: '110px', style: 'bold'},
                {heading: 'Type', accessor: 'type', cellWidth: '110px',
                    valueFunc: (row, value) => ({check: 'Check', credit_card: 'Credit Card', ach: 'ACH', wire: 'Wire'}[value] || value)},
                {heading: 'Reference', type: 'multiple', rows: [
                    {heading: 'Check #', style: 'subtext', accessor: 'check_number'},
                    {heading: 'Last 4', style: 'subtext', accessor: 'last_digits'},
                    {heading: 'Ref #', style: 'subtext', accessor: 'reference_number'},
                    {heading: 'Memo', style: 'subtext', accessor: 'memo'},
                ]},
                {heading: 'Payment Amount', type: 'currency', accessor: 'amount'},
                {heading: 'Applied', type: 'currency', accessor: 'payments_sum'},
                {heading: 'Available Credit', type: 'currency', accessor: 'available_credit', style: 'bold'},
            ]
        }
    },
    {key: 'customer_credits',
        type: 'references',
        heading: 'Credits',
        // Open payments (transactions) for this customer that still have unapplied money.
        // The list already filters to non-voided payments with available_credit > 0, so every
        // row here is a live credit that can still be applied to an invoice.
        readPrivileges: [Privileges.LIST_CUSTOMER_CREDITS],
        dataUrl: '/lists/customer_credits?customer_id=:id',
        tableProps: {
            rowURL: '/transactions/:id',
            columns: [
                {heading: 'Posted', accessor: 'created_at', type: 'date', dateFormat: 'MMM DD, YYYY', cellWidth: '100px'},
                {heading: 'Payment #', accessor: 'transaction_number', cellWidth: '110px', style: 'bold'},
                {heading: 'Type', accessor: 'type', cellWidth: '110px',
                    valueFunc: (row, value) => ({check: 'Check', credit_card: 'Credit Card', ach: 'ACH', wire: 'Wire'}[value] || value)},
                {heading: 'Reference', type: 'multiple', rows: [
                    {heading: 'Check #', style: 'subtext', accessor: 'check_number'},
                    {heading: 'Last 4', style: 'subtext', accessor: 'last_digits'},
                    {heading: 'Ref #', style: 'subtext', accessor: 'reference_number'},
                    {heading: 'Memo', style: 'subtext', accessor: 'memo'},
                ]},
                {heading: 'Payment Amount', type: 'currency', accessor: 'amount'},
                {heading: 'Applied', type: 'currency', accessor: 'payments_sum'},
                {heading: 'Available Credit', type: 'currency', accessor: 'available_credit', style: 'bold'},
            ]
        }
    },
];

// const form = [
//     {key: 'customer_number', label: 'Customer Number', static: true, readOnly: true},
//     {key: 'name', label: 'Name', required: true},
//     {key: 'care_of', label: 'C/O', defaultValue: 'Accounts Payable'},
//     {key: 'code', label: 'Code'},
//     {key: 'address', label: 'Address'},
//     {key: 'address2', label: 'Address Line 2'},
//     {key: 'city', label: 'City'},
//     {key: 'state', 
//         defaultValue: 'Florida',
//         label: 'State',
//         type: 'options',
//         options: [
//             {value: 'Alabama', label: 'Alabama'},
//             {value: 'Alaska', label: 'Alaska'},
//             {value: 'Arizona', label: 'Arizona'},
//             {value: 'Arkansas', label: 'Arkansas'},
//             {value: 'California', label: 'California'},
//             {value: 'Colorado', label: 'Colorado'},
//             {value: 'Connecticut', label: 'Connecticut'},
//             {value: 'Delaware', label: 'Delaware'},
//             {value: 'Florida', label: 'Florida'},
//             {value: 'Georgia', label: 'Georgia'},
//             {value: 'Hawaii', label: 'Hawaii'},
//             {value: 'Idaho', label: 'Idaho'},
//             {value: 'Illinois', label: 'Illinois'},
//             {value: 'Indiana', label: 'Indiana'},
//             {value: 'Iowa', label: 'Iowa'},
//             {value: 'Kansas', label: 'Kansas'},
//             {value: 'Kentucky', label: 'Kentucky'},
//             {value: 'Louisiana', label: 'Louisiana'},
//             {value: 'Maine', label: 'Maine'},
//             {value: 'Maryland', label: 'Maryland'},
//             {value: 'Massachusetts', label: 'Massachusetts'},
//             {value: 'Michigan', label: 'Michigan'},
//             {value: 'Minnesota', label: 'Minnesota'},
//             {value: 'Mississippi', label: 'Mississippi'},
//             {value: 'Missouri', label: 'Missouri'},
//             {value: 'Montana', label: 'Montana'},
//             {value: 'Nebraska', label: 'Nebraska'},
//             {value: 'Nevada', label: 'Nevada'},
//             {value: 'New Hampshire', label: 'New Hampshire'},
//             {value: 'New Jersey', label: 'New Jersey'},
//             {value: 'New Mexico', label: 'New Mexico'},
//             {value: 'New York', label: 'New York'},
//             {value: 'North Carolina', label: 'North Carolina'},
//             {value: 'North Dakota', label: 'North Dakota'},
//             {value: 'Ohio', label: 'Ohio'},
//             {value: 'Oklahoma', label: 'Oklahoma'},
//             {value: 'Oregon', label: 'Oregon'},
//             {value: 'Pennsylvania', label: 'Pennsylvania'},
//             {value: 'Rhode Island', label: 'Rhode Island'},
//             {value: 'South Carolina', label: 'South Carolina'},
//             {value: 'South Dakota', label: 'South Dakota'},
//             {value: 'Tennessee', label: 'Tennessee'},
//             {value: 'Texas', label: 'Texas'},
//             {value: 'Utah', label: 'Utah'},
//             {value: 'Vermont', label: 'Vermont'},
//             {value: 'Virginia', label: 'Virginia'},
//             {value: 'Washington', label: 'Washington'},
//             {value: 'West Virginia', label: 'West Virginia'},
//             {value: 'Wisconsin', label: 'Wisconsin'},
//             {value: 'Wyoming', label: 'Wyoming'}
//         ],
//     },
//     {key: 'zipcode', label: 'Zipcode'},

//     {key: 'tax_exempt', label: 'Tax Exempt'},

//     {key: 'requires_po', label: 'Requires PO#', type: 'boolean'},
//     {key: 'payment_term', label: 'Payment Term', type: 'options', required:true, options: [
//         {value: 'COD', label: 'COD'},
//         {value: 'NET30', label: 'Net 30'},
//         {value: 'NET60', label: 'Net 60'},
//         {value: 'NET90', label: 'Net 90'},
//     ]},
//     {key: 'credit_hold', label: 'Credit Hold', type: 'boolean'},

//     {key: 'contacts',
//         type: 'references',
//         heading: 'Contacts',
//         createLabel: 'Add New Contact',
//         primaryCreate: false,
//         createUrl: (values) => { return `/customers/${values.id}/contacts` },
//         dataUrl: '/customers/:id/contacts',
//         formFields: [
//                 {key: 'name',  
//                     required: true,
//                 },
//                 {key: 'title',  
//                 },
//                 {key: 'phone', label: 'Office Phone',
//                 },
//                 {key: 'mobile',  
//                 },
//                 {key: 'fax',  
//                 },
//                 {key: 'email',  
//                 },

//         ],
//         tableProps: {
//             rowURL: '/contacts/:id',
//             columns: [
//                 {heading: 'Name', 
//                     accessor: 'name',
//                 },
//                 {heading: 'Title', 
//                     accessor: 'title',
//                 },
//                 {heading: 'Office', 
//                     accessor: 'phone',
//                     valueFunc: ((d,v) => {
//                         return formatPhoneNumber(v) || v;
//                     })
//                 },
//                 {heading: 'Mobile', 
//                     accessor: 'mobile',
//                     valueFunc: ((d,v) => {
//                         return formatPhoneNumber(v) || v;
//                     })
//                 },
//                 {heading: 'Fax', 
//                     accessor: 'fax',
//                     valueFunc: ((d,v) => {
//                         return formatPhoneNumber(v) || v;
//                     })
//                 },
//                 {heading: 'Email', 
//                     accessor: 'email',
//                 },
//             ]
//         }
//     },

//     {key: 'sites',
//         type: 'references',
//         heading: 'Sites',
//         createLabel: 'Create New Site',
//         modalValuesTransform: (values) => { 
//             return {billing_customer_id: values.id}; 
//         },
//         create: {
//             label: 'Create New Site',
//             url: (values) => { return `/sites` },
//             formActions: siteCreateActions, 
//         },
//         addExisting: {
//             label: 'Add Existing Site',
//             searchUrl: '/sites',
//             searchFields: ['id', 'name', 'address', 'address2', 'city', 'state', 'zipcode', 'parent_site_name'],
//             optionValue: (site) => {
//                 return site.id;
//             },
//             optionContent: (site) => {
//                 const { address2, address, city, state, zipcode, name, parent_site_name } = site;
//                 return <div>
//                     <span style={{display: 'block', fontWeight: '600'}}>{name}</span>
//                     <span style={{display: 'block', fontStyle: 'italics', fontSize: '12px'}}>{address}, {address2}, {city}, {state}, {zipcode}</span>
//                     <span style={{fontStyle: 'italics', fontSize: '12px'}}>{parent_site_name}</span>
//                 </div>
//             },
//             onAdd: (values, optionValue) => {
//                 let params = { customer_id : values.id, site_id: optionValue };
//                 return api.rpc('addCustomerSiteDefaultBilling', params);
//             }
//         },
//         primaryCreate: false,
//         createUrl: (values) => { return `/sites` },
//         dataUrl: '/customers/:id/sites?is_default_billing_customer=true',
//         formFields: siteForm,
//         tableProps: {
//             rowURL: '/sites/:id', 
//             columns: [{heading: 'Site Info', type:'multiple', rows: [
//                         {heading: 'Name', style: 'bold', accessor:'name'},
//                         {heading: 'Address', separator: ', ', style: 'subtext', accessor:['address', 'address2', 'city', 'state', 'zipcode']},
//                         {heading: 'Parent Site', style: 'subtext', accessor:'parent_site_name'}
//                        ]
//             },
//             {heading: 'Site Credit Hold', accessor: 'customer_site_has_credit_hold', type: 'boolean'}
//             ],
//         }
//     },
// ];


export default {
    defaults: {
        name: 'Customer',
        color: colors.customers,
        formFields: form,
    },

    view: {
        formActions: [
            {   label: 'Download Open Balance Report',
                icon: 'file',
                privileges: [Privileges.FILE_READ_CUSTOMER_OPEN_BALANCES, Privileges.READ_CUSTOMERS_TOTAL_OUTSTANDING_PAYMENT_REQUEST_BALANCES, Privileges.READ_CUSTOMERS_TOTAL_BALANCE_TO_FINISH],
                method: async ({values, history, inputValues}) => {
                    api.download(`/files/customer_open_balances?customer_id=${values.id}`)
                }
            },
            {   label: 'Download Open Balance Report (by Value)',
                icon: 'file',
                privileges: [Privileges.FILE_READ_CUSTOMER_OPEN_BALANCES, Privileges.READ_CUSTOMERS_TOTAL_OUTSTANDING_PAYMENT_REQUEST_BALANCES, Privileges.READ_CUSTOMERS_TOTAL_BALANCE_TO_FINISH],
                method: async ({values, history, inputValues}) => {
                    api.download(`/files/customer_open_balances?customer_id=${values.id}&sort=value`)
                }
            }
        ],
        remarks: {
            header: (values) => values.name,
        },
    },

    search: {
        searchBy: ['name', 'customer number', 'address'],
        fields: ['id', 'name', 'total_outstanding_payment_request_balances', 'total_balance_to_finish',
                 'total_invoice_open_balances', 'total_invoice_amount', 'created_at'],
        // Combined columns for the 'All Customers' scope: sum the project payment-request
        // figures with the non-project invoice figures, the same way the backend report
        // folds inv_open_total into the open-balance total and inv_req_total into the
        // requested/invoiced total (resources/files/customer_files.js).
        dataTransform: (row) => ({
            ...row,
            combined_open_balance: (parseFloat(row.total_outstanding_payment_request_balances) || 0)
                                 + (parseFloat(row.total_invoice_open_balances) || 0),
            combined_pending_invoiced: (parseFloat(row.total_balance_to_finish) || 0)
                                     + (parseFloat(row.total_invoice_amount) || 0),
        }),
        filterOptions: [
             {label: 'Projects', filters: { has_projects: true }},
             {label: 'Non-Projects', filters: { has_open_invoices: true }},
             {label: 'All', filters: {}}
        ],
        links: ['new',
            {downloadURL: (state) => `/files/customer_open_balances?scope=${customerScope(state)}`,
                title: 'Download Open Balance Report',
                requiredPrivileges: [Privileges.FILE_READ_CUSTOMER_OPEN_BALANCES, Privileges.READ_CUSTOMERS_TOTAL_OUTSTANDING_PAYMENT_REQUEST_BALANCES, Privileges.READ_CUSTOMERS_TOTAL_BALANCE_TO_FINISH]
            },
            {downloadURL: (state) => `/files/customer_open_balances?scope=${customerScope(state)}&sort=value`,
                title: 'Download Open Balance Report (by Value)',
                requiredPrivileges: [Privileges.FILE_READ_CUSTOMER_OPEN_BALANCES, Privileges.READ_CUSTOMERS_TOTAL_OUTSTANDING_PAYMENT_REQUEST_BALANCES, Privileges.READ_CUSTOMERS_TOTAL_BALANCE_TO_FINISH]
            },
        ],
        tableProps: {
            rowURL: '/customers/:id',
            // Columns vary with the selected customer-type filter so the on-screen figures
            // match the scoped report the Download buttons produce:
            //   Project Customers (default) -> project payment-request balances
            //   Non-Project Customers       -> non-project invoice balances
            //   All Customers               -> combined (project + invoice) totals
            // The two invoice/combined columns reuse the existing balance privileges so they
            // only render for users already permitted to see the project balance columns.
            columns: (option) => {
                const nameCol = {heading: 'Name', accessor: 'name'};
                const createdCol = {accessor: 'created_at', type: 'date', dateFormat: 'MMM DD, YYYY'};
                const openBalPrivs = [Privileges.READ_CUSTOMERS_TOTAL_OUTSTANDING_PAYMENT_REQUEST_BALANCES];
                const pendingPrivs = [Privileges.READ_CUSTOMERS_TOTAL_BALANCE_TO_FINISH];

                if(option === 'Non-Projects') {
                    return [nameCol,
                        {heading: 'Invoice Open Balance', type: 'currency',
                         accessor: 'total_invoice_open_balances', privileges: openBalPrivs},
                        {heading: 'Invoice Total', type: 'currency',
                         accessor: 'total_invoice_amount', privileges: pendingPrivs},
                        createdCol];
                }

                if(option === 'All') {
                    return [nameCol,
                        {heading: 'Open Balance', type: 'currency',
                         accessor: 'combined_open_balance', privileges: openBalPrivs},
                        {heading: 'Pending to Request + Invoiced', type: 'currency',
                         accessor: 'combined_pending_invoiced', privileges: pendingPrivs},
                        createdCol];
                }

                // Project Customers (default) — original project-only columns.
                return [nameCol,
                    {heading: 'Open Balances', type: 'currency',
                     accessor: 'total_outstanding_payment_request_balances', privileges: openBalPrivs},
                    {heading: 'Total Pending to Request', type: 'currency',
                     accessor: 'total_balance_to_finish', privileges: pendingPrivs},
                    createdCol];
            },
            showHeading: false,
        }
    }
}
